import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { 
  getFilteredProductCards,
  getCategoryTheme
} from '../config/productUtils';
import { getProductMainImage } from '../config/productImages';
import { ShoppingBag, Zap } from 'lucide-react';
import LoginModal from '../components/LoginModal';

const Shop = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  
  // 获取登录状态和用户信息
  const { isLoggedIn, user } = useSelector((state) => state.user);
  
  // 主分类选择
  const [selectedCategory, setSelectedCategory] = useState('all');
  
  // 登录弹窗状态
  const [isLoginModalOpen, setIsLoginModalOpen] = useState(false);
  
  // 统一的购买处理函数
  const handleBuyNow = (e, card) => {
    if (e) {
      e.stopPropagation();
    }
    
    // 检查用户是否已登录
    if (!isLoggedIn) {
      setIsLoginModalOpen(true);
      return;
    }
    
    // 获取用户邮箱
    const userEmail = user?.uid || user?.email || 'user@example.com';
    
    // 准备订单确认页需要的参数
    const params = new URLSearchParams({
      email: userEmail,
      productId: card.id, // 使用配置文件中的id字段
      quantity: '1', // 默认数量为1
      version: card.label || card.key, // 版本信息
      productName: card.productName,
      productType: card.productCategoryId,
      price: card.price.toString()
    });
    
    // 导航到订单确认页
    navigate(`/detail/info?${params.toString()}`);
  };
  
  // 登录成功回调
  const handleLoginSuccess = () => {
    setIsLoginModalOpen(false);
    // 登录成功后可以做其他操作，比如显示提示信息
  };
  
  // 使用统一的JSON配置获取产品卡片（注释掉未使用的变量）
  // const productCards = getAllProductCards();
  
  // 产品分类（只显示主分类，不显示子分类）
  const categories = [
    { id: 'all', name: '全部', icon: '🎯', color: 'purple' },
    { id: 'aimonkey', name: 'AIMonkey会员', icon: '🤖', color: 'blue' },
    { id: 'diamond', name: '钻石', icon: '💎', color: 'pink' },
    { id: 'testing', name: 'Gemini测试天卡', icon: '✨', color: 'green' }
    // { id: 'official_recharge', name: '官网成品号/代充值', icon: '🏢', color: 'green' } // 暂时注释掉
  ].map(cat => ({
    ...cat,
    name: t(`shop.categories.${cat.id}`, cat.name) // 支持i18n
  }));
  
  
  // 过滤产品卡片 - AIMonkey显示所有子产品，其他正常过滤
  const getFilteredCards = () => {
    if (selectedCategory === 'all') {
      return getFilteredProductCards('all');
    } else if (selectedCategory === 'aimonkey') {
      // AIMonkey分类下，显示所有相关产品
      const memberCards = getFilteredProductCards('aimonkey_member');
      const vipCards = getFilteredProductCards('aimonkey_vip');
      return [...memberCards, ...vipCards];
    // } else if (selectedCategory === 'official_recharge') {
    //   // 官网账户/带充值分类，显示对应产品 - 暂时注释掉
    //   return getFilteredProductCards('diamond_official_recharge');
    } else {
      return getFilteredProductCards(selectedCategory);
    }
  };
  
  const filteredCards = getFilteredCards();
  
  // 按子分类分组AIMonkey产品
  const groupAIMonkeyCards = () => {
    if (selectedCategory !== 'aimonkey') return null;
    
    return {
      member: getFilteredProductCards('aimonkey_member'),
      vip: getFilteredProductCards('aimonkey_vip')
    };
  };
  
  // 为"全部"分类按产品类型分组
  const groupAllCards = () => {
    if (selectedCategory !== 'all') return null;

    return {
      aimonkey_member: {
        title: 'AIMonkey 会员',
        description: '基础套餐，适合日常使用需求',
        icon: '👤',
        color: 'blue',
        cards: getFilteredProductCards('aimonkey_member')
      },
      aimonkey_vip: {
        title: 'AIMonkey VIP大会员',
        description: '高级套餐，无限制使用所有AI服务',
        icon: '👑',
        color: 'purple',
        cards: getFilteredProductCards('aimonkey_vip')
      },
      diamond: {
        title: '钻石',
        description: '最高端服务，专属客户经理和企业级支持',
        icon: '💎',
        color: 'pink',
        cards: getFilteredProductCards('diamond_service')
      },
      testing: {
        title: 'Gemini测试天卡',
        description: 'Google Gemini AI测试服务，体验最新的多模态AI能力',
        icon: '✨',
        color: 'green',
        cards: getFilteredProductCards('testing')
      }
      // diamond_official_recharge: {  // 暂时注释掉官网成品号/代充值
      //   title: '官网成品号/代充值',
      //   description: '官方ChatGPT Plus成品号和代充值服务，统一17.99美金',
      //   icon: '🏢',
      //   color: 'green',
      //   cards: getFilteredProductCards('diamond_official_recharge')
      // }
    };
  };
  
  // 按子分类分组钻石产品
  const groupDiamondCards = () => {
    if (selectedCategory !== 'diamond') return null;
    
    return {
      service: getFilteredProductCards('diamond_service')
      // 移除official_recharge，因为它现在是独立分类
    };
  };
  
  const aimonkeyGroups = groupAIMonkeyCards();
  const allGroups = groupAllCards();
  const diamondGroups = groupDiamondCards();

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-amber-50">
      {/* Hero Section - 精简版，移除按钮 */}
      <div className="relative overflow-hidden bg-gradient-to-br from-purple-400 via-purple-500 to-red-500">
        {/* 装饰性背景元素 */}
        <div className="absolute -top-24 -right-24 w-32 sm:w-48 h-32 sm:h-48 bg-white/10 rounded-full blur-3xl"></div>
        <div className="absolute -bottom-24 -left-24 w-32 sm:w-48 h-32 sm:h-48 bg-white/10 rounded-full blur-3xl"></div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
          <div className="text-center">
            <div className="flex justify-center mb-3 sm:mb-4">
              <div className="bg-white/20 rounded-full p-2 sm:p-3 animate-float">
                <ShoppingBag className="w-8 h-8 sm:w-10 sm:h-10 text-white" />
              </div>
            </div>
            <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-3 sm:mb-4 px-4">
              {t('shop.hero.title', 'AI产品商城')}
            </h1>
            <div className="w-16 sm:w-24 h-0.5 sm:h-1 bg-white/50 mx-auto rounded-full"></div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* 分类标签 - 移除标题和描述，直接显示按钮 */}
        <div className="mb-8 sm:mb-12" id="products-section">
          {/* 主分类按钮 - 左对齐，响应式布局 */}
          <div className="flex flex-wrap gap-2 sm:gap-4">
            {categories.map((cat) => {
              const isSelected = selectedCategory === cat.id;
              
              // 使用标准的颜色主题
              const getTheme = (color) => {
                const colorMap = {
                  purple: { bg: 'from-purple-500 to-purple-600', border: 'border-purple-200', shadow: 'shadow-purple-200' },
                  blue: { bg: 'from-blue-500 to-blue-600', border: 'border-blue-200', shadow: 'shadow-blue-200' },
                  green: { bg: 'from-green-500 to-green-600', border: 'border-green-200', shadow: 'shadow-green-200' },
                  pink: { bg: 'from-pink-500 to-pink-600', border: 'border-pink-200', shadow: 'shadow-pink-200' },
                  purple: { bg: 'from-purple-500 to-indigo-600', border: 'border-purple-200', shadow: 'shadow-purple-200' },
                  red: { bg: 'from-red-500 to-red-600', border: 'border-red-200', shadow: 'shadow-red-200' }
                };
                return colorMap[color] || colorMap.purple;
              };
              
              const theme = getTheme(cat.color);
              
              return (
                <button
                  key={cat.id}
                  onClick={() => setSelectedCategory(cat.id)}
                  className={`
                    group relative px-4 sm:px-8 py-3 sm:py-4 rounded-xl sm:rounded-2xl font-bold text-sm sm:text-base transition-all duration-300 border-2 min-w-[100px] sm:min-w-[140px]
                    ${isSelected
                      ? `bg-gradient-to-r ${theme.bg} text-white shadow-xl ${theme.shadow}/50 transform scale-110 z-10`
                      : `bg-white text-gray-700 hover:bg-gray-50 ${theme.border} hover:shadow-lg hover:scale-105 shadow-md`
                    }
                  `}
                >
                  <div className="flex items-center justify-center gap-2 sm:gap-3">
                    <span className="text-xl sm:text-2xl">{cat.icon}</span>
                    <span className="whitespace-nowrap">{cat.name}</span>
                  </div>
                  
                  {/* 选中状态的发光效果 */}
                  {isSelected && (
                    <div className="absolute inset-0 bg-gradient-to-r opacity-20 rounded-2xl animate-pulse"
                         style={{background: `linear-gradient(to right, ${theme.bg.replace('from-', '').replace(' to-', ', ')})`}}></div>
                  )}
                  
                  {/* 悬停装饰效果 */}
                  {!isSelected && (
                    <div className="absolute inset-0 bg-gradient-to-r opacity-0 group-hover:opacity-10 transition-opacity duration-300 rounded-2xl"
                         style={{background: `linear-gradient(to right, ${theme.bg.replace('from-', '').replace(' to-', ', ')})`}}></div>
                  )}
                </button>
              );
            })}
          </div>
        </div>
        
        {/* 产品网格 - 优化间距和布局密度 */}
        <div>
          {/* 产品展示区域 */}
          {selectedCategory === 'all' && allGroups ? (
            // 全部分类按产品类型分组显示
            <div className="space-y-12">
              {Object.entries(allGroups).map(([groupKey, group]) => (
                group.cards.length > 0 && (
                  <div key={groupKey}>
                    {/* 合并的产品标题和说明区域 */}
                    <div className={`mb-4 sm:mb-6 p-4 sm:p-6 rounded-xl sm:rounded-2xl border-2 ${
                      group.color === 'blue' ? 'bg-blue-50 border-blue-200' :
                      group.color === 'purple' ? 'bg-gradient-to-br from-purple-50 via-indigo-50 to-blue-50 border-purple-300' :
                      group.color === 'pink' ? 'bg-pink-50 border-pink-200' :
                      group.color === 'green' ? 'bg-gradient-to-br from-green-50 via-emerald-50 to-teal-50 border-green-300' :
                      group.color === 'red' ? 'bg-red-50 border-red-200' :
                      'bg-purple-50 border-purple-200'
                    }`}>
                      <div className="flex flex-col sm:flex-row items-start gap-3 sm:gap-4">
                        <div className={`w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br ${
                          group.color === 'blue' ? 'from-blue-500 to-blue-600' :
                          group.color === 'purple' ? 'from-purple-500 to-indigo-600 shadow-purple-300' :
                          group.color === 'pink' ? 'from-pink-500 to-pink-600' :
                          group.color === 'green' ? 'from-green-500 to-emerald-600 shadow-green-300' :
                          group.color === 'red' ? 'from-red-500 to-red-600' :
                          'from-purple-500 to-purple-600'
                        } rounded-xl sm:rounded-2xl flex items-center justify-center shadow-lg flex-shrink-0`}>
                          <span className="text-xl sm:text-2xl">{group.icon}</span>
                        </div>
                        <div className="flex-1 w-full">
                          <h4 className="text-lg sm:text-xl font-bold text-gray-900 mb-2 sm:mb-3">{group.title}</h4>
                          <p className={`text-xs sm:text-sm leading-relaxed ${
                            group.color === 'blue' ? 'text-blue-700' :
                            group.color === 'purple' ? 'text-purple-700 font-medium' :
                            group.color === 'pink' ? 'text-pink-700' :
                            group.color === 'green' ? 'text-green-700 font-medium' :
                            group.color === 'red' ? 'text-red-700' :
                            'text-purple-700'
                          }`}>
                            {group.description}
                          </p>
                        </div>
                      </div>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
                      {group.cards.map((card, index) => (
                        // 不同产品使用专用卡片
                        card.productCategoryId === 'diamond_service' ? (
                          <DiamondCard key={`${groupKey}-${card.productCategoryId}-${card.key}`} card={card} index={index} onBuyNow={handleBuyNow} />
                        ) : card.productCategoryId === 'gemini_testing' ? (
                          <GeminiCard key={`${groupKey}-${card.productCategoryId}-${card.key}`} card={card} index={index} onBuyNow={handleBuyNow} />
                        ) : (
                          <ProductCard key={`${groupKey}-${card.productCategoryId}-${card.key}`} card={card} index={index} onBuyNow={handleBuyNow} />
                        )
                      ))}
                    </div>
                  </div>
                )
              ))}
            </div>
          ) : selectedCategory === 'aimonkey' && aimonkeyGroups ? (
            // AIMonkey分组显示
            <div className="space-y-12">
              {/* 会员产品组 */}
              {aimonkeyGroups.member.length > 0 && (
                <div>
                  {/* 合并的产品标题和说明区域 */}
                  <div className="mb-6 p-6 rounded-2xl border-2 bg-blue-50 border-blue-200">
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-blue-600 rounded-2xl flex items-center justify-center shadow-lg flex-shrink-0">
                        <span className="text-2xl">👤</span>
                      </div>
                      <div className="flex-1">
                        <h4 className="text-xl font-bold text-gray-900 mb-3">会员</h4>
                        <p className="text-sm leading-relaxed text-blue-700">
                          基础套餐，适合日常使用需求。提供GPT和Sora两个AI车队的访问权限，配备24小时技术保障，是入门AI服务的最佳选择。性价比高，功能实用。
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
                    {aimonkeyGroups.member.map((card, index) => (
                      // AIMonkey产品使用通用卡片
                      <ProductCard key={`member-${card.productCategoryId}-${card.key}`} card={card} index={index} onBuyNow={handleBuyNow} />
                    ))}
                  </div>
                </div>
              )}

              {/* VIP大会员产品组 */}
              {aimonkeyGroups.vip.length > 0 && (
                <div>
                  {/* 合并的产品标题和说明区域 */}
                  <div className="mb-6 p-6 rounded-2xl border-2 bg-purple-50 border-purple-200">
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-purple-600 rounded-2xl flex items-center justify-center shadow-lg flex-shrink-0">
                        <span className="text-2xl">👑</span>
                      </div>
                      <div className="flex-1">
                        <h4 className="text-xl font-bold text-gray-900 mb-3">VIP大会员</h4>
                        <p className="text-sm leading-relaxed text-purple-700">
                          高级套餐，无限制使用所有AI服务。享受全车队权限，包含GPT、Sora、Claude、Grok的完整访问权，配备VIP专属客服支持，是重度AI用户的最佳选择。
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
                    {aimonkeyGroups.vip.map((card, index) => (
                      // AIMonkey VIP产品使用通用卡片
                      <ProductCard key={`vip-${card.productCategoryId}-${card.key}`} card={card} index={index} onBuyNow={handleBuyNow} />
                    ))}
                  </div>
                </div>
              )}
            </div>
          ) : selectedCategory === 'diamond' && diamondGroups ? (
            // 钻石分组显示 - 只显示钻石服务，不显示官网账户
            <div className="space-y-12">
              {/* 钻石服务组 */}
              {diamondGroups.service.length > 0 && (
                <div>
                  {/* 合并的产品标题和说明区域 */}
                  <div className="mb-6 p-6 rounded-2xl border-2 bg-pink-50 border-pink-200">
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 bg-gradient-to-br from-pink-500 to-pink-600 rounded-2xl flex items-center justify-center shadow-lg flex-shrink-0">
                        <span className="text-2xl">💎</span>
                      </div>
                      <div className="flex-1">
                        <h4 className="text-xl font-bold text-gray-900 mb-3">钻石服务</h4>
                        <p className="text-sm leading-relaxed text-pink-700">
                          最高端的AI服务套餐，专属客户经理和企业级支持。包含所有AI模型的高级权限、无限制使用、定制化服务方案，为追求极致体验的专业用户量身打造。
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
                    {diamondGroups.service.map((card, index) => (
                      // 钻石产品使用专用卡片
                      <DiamondCard key={`service-${card.productCategoryId}-${card.key}`} card={card} index={index} onBuyNow={handleBuyNow} />
                    ))}
                  </div>
                </div>
              )}
            </div>
          // ) : selectedCategory === 'official_recharge' ? (  // 暂时注释掉官网充值分类显示
          //   // 官网账户/带充值单独显示
          //   <div>
          //     {/* 合并的产品标题和说明区域 */}
          //     <div className="mb-6 p-6 rounded-2xl border-2 bg-green-50 border-green-200">
          //       <div className="flex items-start gap-4">
          //         <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-green-600 rounded-2xl flex items-center justify-center shadow-lg flex-shrink-0">
          //           <span className="text-2xl">🏢</span>
          //         </div>
          //         <div className="flex-1">
          //           <h4 className="text-xl font-bold text-gray-900 mb-3">官网成品号/代充值</h4>
          //           <p className="text-sm leading-relaxed text-green-700">
          //             官方ChatGPT Plus成品号和代充值服务，统一17.99美金价格透明。提供独享成品账号和专业代充值两种选择，支持官网、APP多端登录，30天质保服务。
          //           </p>
          //         </div>
          //       </div>
          //     </div>
          //     
          //     <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-3 gap-6">
          //       {filteredCards.map((card, index) => (
          //         // 官网账户产品使用通用卡片
          //         <ProductCard key={`${card.productCategoryId}-${card.key}`} card={card} index={index} onBuyNow={handleBuyNow} />
          //       ))}
          //     </div>
          //   </div>
          ) : (
            // 其他分类常规显示
            <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-3 gap-6">
              {filteredCards.map((card, index) => (
                // 不同产品使用专用卡片
                card.productCategoryId === 'gemini_testing' ? (
                  <GeminiCard key={`${card.productCategoryId}-${card.key}`} card={card} index={index} onBuyNow={handleBuyNow} />
                ) : (
                  <ProductCard key={`${card.productCategoryId}-${card.key}`} card={card} index={index} onBuyNow={handleBuyNow} />
                )
              ))}
            </div>
          )}
        </div>
      </div>
      
      
      {/* 登录弹窗 */}
      <LoginModal 
        isOpen={isLoginModalOpen}
        onClose={() => setIsLoginModalOpen(false)}
        onLogin={handleLoginSuccess}
      />
    </div>
  );
};

// 钻石产品专用简洁卡片组件
const DiamondCard = ({ card, index, onBuyNow }) => {
  const { t } = useTranslation();

  return (
    <div
      className="group h-full flex flex-col"
      style={{
        animation: `fadeInUp 0.6s ease-out ${index * 0.08}s both`
      }}
    >
      <div
        className={`
          relative bg-gradient-to-br from-pink-50 via-white to-purple-50 rounded-3xl overflow-hidden
          shadow-xl hover:shadow-2xl transition-all duration-500 ease-out
          transform hover:scale-[1.02] hover:-translate-y-1
          border-2 border-pink-200 backdrop-blur-sm
          flex flex-col cursor-pointer
          ${card.stock > 0 ? 'hover:shadow-pink-500/20' : 'opacity-75 cursor-not-allowed'}
          flex-1
        `}
      >
        {/* 装饰性背景光晕 */}
        <div className="absolute -top-8 -right-8 w-16 h-16 bg-gradient-to-br from-pink-200 to-purple-200 rounded-full opacity-20 blur-2xl group-hover:opacity-30 transition-opacity duration-500"></div>
        <div className="absolute -bottom-6 -left-6 w-12 h-12 bg-gradient-to-br from-purple-200 to-pink-200 rounded-full opacity-15 blur-xl group-hover:opacity-25 transition-opacity duration-500"></div>

        <div className="p-6 flex flex-col h-full relative z-10 text-center">
          {/* 产品标题 */}
          <div className="mb-4">
            <div className="inline-flex px-4 py-2 rounded-xl text-sm font-bold bg-gradient-to-r from-pink-500 to-purple-500 text-white shadow-lg">
              {card.diamondCount ? `${card.diamondCount.toLocaleString()}钻石` : card.label}
            </div>
          </div>

          {/* 价格 */}
          <div className="mb-6">
            <span className="text-3xl font-bold bg-gradient-to-r from-pink-600 to-purple-600 bg-clip-text text-transparent">
              ${card.price}
            </span>
          </div>

          {/* 钻石图标装饰 */}
          <div className="flex-1 flex items-center justify-center">
            <div className="relative">
              <div className="w-16 h-16 bg-gradient-to-br from-pink-100 to-purple-100 rounded-2xl flex items-center justify-center shadow-lg">
                <span className="text-4xl animate-pulse">💎</span>
              </div>
              <div className="absolute -top-2 -right-2 w-6 h-6 bg-gradient-to-br from-pink-500 to-purple-500 rounded-full flex items-center justify-center shadow-lg animate-bounce">
                <span className="text-white text-xs font-bold">✓</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* 购买按钮 - 移到卡片外部下方，显示价格 */}
      <button
        onClick={(e) => onBuyNow(e, card)}
        className={`
          w-full mt-4 py-3 sm:py-3.5 px-4 rounded-xl font-bold text-sm transition-all duration-300
          flex items-center justify-between relative overflow-hidden
          ${card.stock > 0
            ? 'bg-gradient-to-r from-pink-500 to-purple-500 text-white hover:shadow-xl hover:scale-105 transform'
            : 'bg-gray-200 text-gray-500 cursor-not-allowed'
          }
        `}
        disabled={card.stock === 0}
      >
        {card.stock > 0 && (
          <div className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/20 to-white/0 -translate-x-full group-hover:translate-x-full transition-transform duration-500"></div>
        )}
        <span className="relative z-10 flex items-center gap-2">
          <ShoppingBag className="w-4 h-4" />
          {card.stock > 0 ? '立即购买' : '暂不可用'}
        </span>
        {card.stock > 0 && (
          <span className="relative z-10 text-base sm:text-lg font-bold">
            ${card.price}
          </span>
        )}
      </button>
    </div>
  );
};

// Gemini产品专用卡片组件 - 精美设计，带logo图片
const GeminiCard = ({ card, index, onBuyNow }) => {
  const { t } = useTranslation();

  return (
    <div
      className="group h-full flex flex-col"
      style={{
        animation: `fadeInUp 0.6s ease-out ${index * 0.08}s both`
      }}
    >
      <div
        className={`
          relative bg-gradient-to-br from-green-50 via-emerald-50 to-teal-50 rounded-3xl overflow-hidden
          shadow-xl hover:shadow-2xl transition-all duration-500 ease-out
          transform hover:scale-[1.02] hover:-translate-y-1
          border-2 border-green-300 backdrop-blur-sm
          flex flex-col cursor-pointer
          ${card.stock > 0 ? 'hover:shadow-green-500/30' : 'opacity-75 cursor-not-allowed'}
          flex-1
        `}
      >
        {/* 装饰性背景光晕 */}
        <div className="absolute -top-8 -right-8 w-20 h-20 bg-gradient-to-br from-green-200 to-emerald-200 rounded-full opacity-20 blur-3xl group-hover:opacity-30 transition-opacity duration-500"></div>
        <div className="absolute -bottom-8 -left-8 w-16 h-16 bg-gradient-to-br from-emerald-200 to-teal-200 rounded-full opacity-15 blur-2xl group-hover:opacity-25 transition-opacity duration-500"></div>

        <div className="p-6 flex flex-col h-full relative z-10">
          {/* Gemini Logo图片 */}
          <div className="mb-4 flex justify-center">
            <div className="relative w-24 h-24 rounded-2xl overflow-hidden shadow-lg border-2 border-green-300 bg-white group-hover:shadow-xl transition-shadow duration-300">
              <img
                src="/images/gemini-main.jpg"
                alt="Gemini AI"
                className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                onError={(e) => {
                  e.target.style.display = 'none';
                  e.target.nextSibling.style.display = 'flex';
                }}
              />
              <div className="w-full h-full hidden items-center justify-center bg-gradient-to-br from-green-100 to-emerald-100">
                <span className="text-4xl">✨</span>
              </div>
              {/* 右上角标签 */}
              <div className="absolute -top-1 -right-1 w-8 h-8 bg-gradient-to-br from-green-500 to-emerald-600 rounded-full flex items-center justify-center shadow-lg">
                <Zap className="w-4 h-4 text-white" />
              </div>
            </div>
          </div>

          {/* 产品标题 */}
          <div className="mb-3 text-center">
            <div className="inline-flex px-3 py-1.5 rounded-xl text-sm font-bold bg-gradient-to-r from-green-500 to-emerald-600 text-white shadow-lg">
              {card.duration || card.label}
            </div>
          </div>

          {/* 价格 */}
          <div className="mb-4 text-center">
            <span className="text-3xl font-bold bg-gradient-to-r from-green-600 to-emerald-600 bg-clip-text text-transparent">
              ${card.price}
            </span>
            <p className="text-xs text-gray-600 mt-1">
              天卡制，有效期{card.duration || '1天'}
            </p>
          </div>

          {/* Gemini特色标签 */}
          <div className="flex flex-wrap gap-1.5 justify-center">
            <span className="px-2 py-1 bg-green-100 text-green-700 rounded-lg text-xs font-medium">
              多模态AI
            </span>
            <span className="px-2 py-1 bg-emerald-100 text-emerald-700 rounded-lg text-xs font-medium">
              智能对话
            </span>
            <span className="px-2 py-1 bg-teal-100 text-teal-700 rounded-lg text-xs font-medium">
              快速响应
            </span>
          </div>
        </div>
      </div>

      {/* 购买按钮 - 移到卡片外部下方，显示价格 */}
      <button
        onClick={(e) => onBuyNow(e, card)}
        className={`
          w-full mt-4 py-3 sm:py-3.5 px-4 rounded-xl font-bold text-sm transition-all duration-300
          flex items-center justify-between relative overflow-hidden
          ${card.stock > 0
            ? 'bg-gradient-to-r from-green-500 to-emerald-600 text-white hover:shadow-xl hover:scale-105 transform'
            : 'bg-gray-200 text-gray-500 cursor-not-allowed'
          }
        `}
        disabled={card.stock === 0}
      >
        {card.stock > 0 && (
          <div className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/30 to-white/0 -translate-x-full group-hover:translate-x-full transition-transform duration-500"></div>
        )}
        <span className="relative z-10 flex items-center gap-2">
          <ShoppingBag className="w-4 h-4" />
          {card.stock > 0 ? '立即体验' : '暂不可用'}
        </span>
        {card.stock > 0 && (
          <span className="relative z-10 text-base sm:text-lg font-bold">
            ${card.price}
          </span>
        )}
      </button>
    </div>
  );
};

// 产品卡片组件 - 重新设计，与项目风格保持一致
const ProductCard = ({ card, index, onBuyNow }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  
  // 获取登录状态和用户信息
  const { isLoggedIn, user } = useSelector((state) => state.user);
  
  // 计算每月价格 - 对钻石积分产品特殊处理
  const monthlyPrice = card.diamondCount 
    ? (card.price / card.diamondCount * 1000).toFixed(3) // 钻石积分按每1000积分的单价计算
    : card.month ? (card.price / card.month).toFixed(2) : card.price;
  
  // 处理查看详情按钮点击
  const handleViewDetails = (e) => {
    if (e) {
      e.stopPropagation();
    }
    navigate(`/product/${card.versionProductId}?type=${card.productCategoryId}`);
  };
  
  // 获取产品图片
  const productImage = getProductMainImage(card.productCategoryId);
  
  // 主题处理现在由productUtils统一管理
  
  const theme = getCategoryTheme(card.productCategoryId);

  return (
    <div
      className="group h-full flex flex-col"
      style={{
        animation: `fadeInUp 0.6s ease-out ${index * 0.08}s both`
      }}
    >
      <div
        className={`
          relative bg-gradient-to-br ${theme.gradient} rounded-3xl overflow-hidden
          shadow-xl hover:shadow-2xl transition-all duration-500 ease-out
          transform hover:scale-[1.02] hover:-translate-y-1
          border-2 ${theme.border} backdrop-blur-sm
          flex flex-col
          ${card.stock > 0 ? 'hover:shadow-blue-500/10' : 'opacity-75'}
          flex-1
        `}
      >
        {/* 多层装饰性背景光晕 */}
        <div className="absolute -top-8 -right-8 w-16 h-16 bg-gradient-to-br from-purple-200 to-pink-200 rounded-full opacity-20 blur-2xl group-hover:opacity-30 transition-opacity duration-500"></div>
        <div className="absolute -bottom-6 -left-6 w-12 h-12 bg-gradient-to-br from-blue-200 to-purple-200 rounded-full opacity-15 blur-xl group-hover:opacity-25 transition-opacity duration-500"></div>
        
        <div className="p-5 flex flex-col h-full relative z-10 backdrop-blur-[1px]">
          {/* AIMonkey产品的AI车队展示 - 作为主要内容，约占60% */}
          {card.productCategoryId.includes('aimonkey') && (
            <div className="flex-1 mb-4 p-4 bg-gradient-to-br from-blue-50/90 via-white/50 to-purple-50/90 rounded-2xl border border-blue-200/50 shadow-lg backdrop-blur-sm group-hover:shadow-xl group-hover:border-blue-300/60 transition-all duration-300">
              <div className="h-full flex flex-col relative">
                {/* 内部装饰光效 */}
                <div className="absolute -top-2 -right-2 w-8 h-8 bg-gradient-to-br from-blue-300/20 to-purple-300/20 rounded-full blur-lg"></div>
                
                {/* AI共享车队 - 标题 */}
                <div className="text-center mb-3 relative">
                  <h3 className="text-lg font-black text-gray-800 mb-1 tracking-tight">
                    <span className="bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                      🚗 AI共享车队
                    </span>
                  </h3>
                  <div className="absolute -bottom-1 left-1/2 transform -translate-x-1/2 w-12 h-0.5 bg-gradient-to-r from-blue-400 to-purple-400 rounded-full"></div>
                </div>
                
                {/* 4个AI模型展示 - 1x4横向布局 */}
                <div className="grid grid-cols-4 gap-2 flex-1">
                  {/* GPT */}
                  <div className="flex flex-col items-center justify-center p-2.5 bg-white/80 backdrop-blur-sm rounded-xl border border-green-200/60 shadow-md hover:shadow-lg hover:scale-105 transition-all duration-300 group/item">
                    <div className="relative mb-2">
                      <div className="w-8 h-8 bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl flex items-center justify-center shadow-inner group-hover/item:shadow-lg transition-shadow duration-300">
                        <img 
                          src="/images/gpt-main.jpg" 
                          alt="GPT" 
                          className="w-6 h-6 object-cover rounded-lg"
                          onError={(e) => {
                            e.target.style.display = 'none';
                            e.target.nextSibling.style.display = 'block';
                          }}
                        />
                        <span className="text-gray-600 text-sm font-bold" style={{display: 'none'}}>G</span>
                      </div>
                      <div className="absolute -top-1 -right-1 w-4 h-4 bg-gradient-to-br from-green-400 to-green-600 rounded-full flex items-center justify-center shadow-lg animate-pulse">
                        <span className="text-white text-xs font-bold">✓</span>
                      </div>
                    </div>
                    <span className="text-xs font-bold text-gray-800 text-center leading-tight">GPT</span>
                    <span className="text-xs text-green-600 font-semibold">可用</span>
                  </div>

                  {/* Sora - 右上 */}
                  <div className="flex flex-col items-center justify-center p-2 bg-white rounded-lg border border-green-200 shadow-sm">
                    <div className="relative mb-1">
                      <div className="w-6 h-6 bg-blue-100 rounded-md flex items-center justify-center">
                        <img 
                          src="/images/sora-main.jpg" 
                          alt="Sora" 
                          className="w-5 h-5 object-cover rounded-sm"
                          onError={(e) => {
                            e.target.style.display = 'none';
                            e.target.nextSibling.style.display = 'block';
                          }}
                        />
                        <span className="text-blue-600 text-xs font-bold" style={{display: 'none'}}>S</span>
                      </div>
                      <div className="absolute -top-0.5 -right-0.5 w-3 h-3 bg-green-500 rounded-full flex items-center justify-center">
                        <span className="text-white text-xs">✓</span>
                      </div>
                    </div>
                    <span className="text-xs font-bold text-gray-800 text-center leading-tight">Sora</span>
                    <span className="text-xs text-green-600">可用</span>
                  </div>

                  {/* Claude - 左下 */}
                  <div className={`flex flex-col items-center justify-center p-2 rounded-lg border shadow-sm ${
                    card.productCategoryId.includes('vip') 
                      ? 'bg-white border-green-200' 
                      : 'bg-gray-100 border-red-200 opacity-60'
                  }`}>
                    <div className="relative mb-1">
                      <div className="w-6 h-6 bg-purple-100 rounded-md flex items-center justify-center">
                        <img 
                          src="/images/claude-main.jpg" 
                          alt="Claude" 
                          className={`w-5 h-5 object-cover rounded-sm ${
                            card.productCategoryId.includes('vip') ? '' : 'grayscale'
                          }`}
                          onError={(e) => {
                            e.target.style.display = 'none';
                            e.target.nextSibling.style.display = 'block';
                          }}
                        />
                        <span className={`text-xs font-bold ${
                          card.productCategoryId.includes('vip') ? 'text-purple-600' : 'text-gray-400'
                        }`} style={{display: 'none'}}>C</span>
                      </div>
                      <div className={`absolute -top-0.5 -right-0.5 w-3 h-3 rounded-full flex items-center justify-center ${
                        card.productCategoryId.includes('vip') 
                          ? 'bg-green-500' 
                          : 'bg-red-500'
                      }`}>
                        <span className="text-white text-xs">
                          {card.productCategoryId.includes('vip') ? '✓' : '✗'}
                        </span>
                      </div>
                    </div>
                    <span className={`text-xs font-bold text-center leading-tight ${
                      card.productCategoryId.includes('vip') ? 'text-gray-800' : 'text-gray-400'
                    }`}>Claude</span>
                    <span className={`text-xs ${
                      card.productCategoryId.includes('vip') ? 'text-green-600' : 'text-red-500'
                    }`}>
                      {card.productCategoryId.includes('vip') ? '可用' : 'VIP专享'}
                    </span>
                  </div>

                  {/* Grok - 右下 */}
                  <div className={`flex flex-col items-center justify-center p-2 rounded-lg border shadow-sm ${
                    card.productCategoryId.includes('vip') 
                      ? 'bg-white border-green-200' 
                      : 'bg-gray-100 border-red-200 opacity-60'
                  }`}>
                    <div className="relative mb-1">
                      <div className="w-6 h-6 bg-purple-100 rounded-md flex items-center justify-center">
                        <img 
                          src="/images/grok-main.jpg" 
                          alt="Grok" 
                          className={`w-5 h-5 object-cover rounded-sm ${
                            card.productCategoryId.includes('vip') ? '' : 'grayscale'
                          }`}
                          onError={(e) => {
                            e.target.style.display = 'none';
                            e.target.nextSibling.style.display = 'block';
                          }}
                        />
                        <span className={`text-xs font-bold ${
                          card.productCategoryId.includes('vip') ? 'text-purple-600' : 'text-gray-400'
                        }`} style={{display: 'none'}}>G</span>
                      </div>
                      <div className={`absolute -top-0.5 -right-0.5 w-3 h-3 rounded-full flex items-center justify-center ${
                        card.productCategoryId.includes('vip') 
                          ? 'bg-green-500' 
                          : 'bg-red-500'
                      }`}>
                        <span className="text-white text-xs">
                          {card.productCategoryId.includes('vip') ? '✓' : '✗'}
                        </span>
                      </div>
                    </div>
                    <span className={`text-xs font-bold text-center leading-tight ${
                      card.productCategoryId.includes('vip') ? 'text-gray-800' : 'text-gray-400'
                    }`}>Grok</span>
                    <span className={`text-xs ${
                      card.productCategoryId.includes('vip') ? 'text-green-600' : 'text-red-500'
                    }`}>
                      {card.productCategoryId.includes('vip') ? '可用' : 'VIP专享'}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* 简洁的价格信息 - 约占25% */}
          <div className="mb-3 p-2.5 bg-white/80 rounded-xl border border-gray-200 shadow-sm">
            <div className="text-center space-y-1.5">
              {/* 产品标签 - 钻石积分产品特殊显示，天卡产品显示天数 */}
              <div className={`inline-flex px-2.5 py-0.5 rounded-lg text-xs font-bold ${theme.tag}`}>
                {card.diamondCount
                  ? `${card.diamondCount.toLocaleString()}钻石积分`
                  : card.month === 0 && card.duration
                    ? card.duration  // 天卡产品显示duration（如"1天"）
                    : `${card.month}个月套餐`
                }
              </div>

              {/* 价格信息 - 缩小 */}
              <div className="flex items-end justify-center gap-2">
                <span className={`text-xl font-bold ${theme.accent}`}>
                  ${card.price}
                </span>
              </div>

              {/* 价格说明 - 钻石积分产品和天卡产品特殊显示 */}
              <div className="text-xs text-gray-600">
                {card.diamondCount
                  ? `单价 $${monthlyPrice}/1000积分`
                  : card.month === 0 && card.duration
                    ? `天卡制，有效期${card.duration}`
                    : card.month > 1
                      ? `月均 $${monthlyPrice}`
                      : `一次性付费`
                }
              </div>
            </div>
          </div>

          {/* 钻石积分产品特殊显示区域 */}
          {card.diamondCount && (
            <div className="mb-2 border-t border-white/50 pt-2">
              <div className="bg-gradient-to-r from-pink-50 via-purple-50 to-blue-50 border border-pink-300 rounded-lg p-3 shadow-sm">
                <div className="flex items-center justify-center gap-3">
                  <div className="relative">
                    <span className="text-2xl animate-pulse">💎</span>
                    <div className="absolute -top-1 -right-1 w-2 h-2 bg-pink-500 rounded-full animate-ping"></div>
                  </div>
                  <div className="text-center">
                    <p className="text-sm font-medium text-pink-800 mb-1">💰 包含积分</p>
                    <div className="flex items-center justify-center gap-1 bg-gradient-to-r from-pink-500 to-purple-500 text-white px-3 py-1 rounded-full">
                      <span className="text-sm font-bold">{card.diamondCount.toLocaleString()}</span>
                      <span className="text-xs">钻石积分</span>
                    </div>
                    <p className="text-xs text-gray-600 mt-1">永久有效，随时使用</p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* 钻石积分奖励 - 紧凑显示，钻石积分产品不显示额外奖励 */}
          {(card.diamondReward && card.diamondReward > 0 && !card.diamondCount) && (
            <div className="mb-2 border-t border-white/50 pt-2">
              <div className="bg-gradient-to-r from-yellow-50 via-purple-50 to-amber-50 border border-purple-300 rounded-lg p-2 shadow-sm">
                <div className="flex items-center justify-center gap-2">
                  <div className="relative">
                    <span className="text-lg animate-pulse">💎</span>
                    <div className="absolute -top-1 -right-1 w-1.5 h-1.5 bg-purple-400 rounded-full animate-ping"></div>
                  </div>
                  <div className="text-center">
                    <p className="text-xs font-medium text-purple-800 mb-0.5">🎁 购买即送</p>
                    <div className="flex items-center justify-center gap-1 bg-gradient-to-r from-purple-500 to-red-500 text-white px-2 py-0.5 rounded-full">
                      <span className="text-xs font-bold">+{card.diamondReward}</span>
                      <span className="text-xs">钻石积分</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* 购买按钮 - 移到卡片外部下方，显示价格 */}
      <button
        onClick={(e) => onBuyNow(e, card)}
        className={`
          w-full mt-4 py-3 sm:py-3.5 px-4 rounded-xl font-bold text-sm transition-all duration-300
          flex items-center justify-between relative overflow-hidden
          ${card.stock > 0
            ? `bg-gradient-to-r ${theme.button} text-white hover:shadow-xl hover:scale-105 transform`
            : 'bg-gray-200 text-gray-500 cursor-not-allowed'
          }
        `}
        disabled={card.stock === 0}
      >
        {card.stock > 0 && (
          <div className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/20 to-white/0 -translate-x-full group-hover:translate-x-full transition-transform duration-500"></div>
        )}
        <span className="relative z-10 flex items-center gap-2">
          <ShoppingBag className="w-4 h-4" />
          {card.stock > 0 ? '立即购买' : '暂不可用'}
        </span>
        {card.stock > 0 && (
          <span className="relative z-10 text-base sm:text-lg font-bold">
            ${card.price}
          </span>
        )}
      </button>
    </div>
  );
};

export default Shop;

// 添加动画样式 - 与项目整体风格保持一致
if (typeof document !== 'undefined') {
  const style = document.createElement('style');
  style.textContent = `
    @keyframes fadeInUp {
      from {
        opacity: 0;
        transform: translateY(20px) scale(0.98);
      }
      to {
        opacity: 1;
        transform: translateY(0) scale(1);
      }
    }
    
    @keyframes shimmer {
      0% { transform: translateX(-100%); }
      100% { transform: translateX(100%); }
    }
    
    @keyframes float {
      0%, 100% { transform: translateY(0px); }
      50% { transform: translateY(-3px); }
    }
    
    .hover\\:scale-102:hover {
      transform: scale(1.02);
    }
    
    .animate-float {
      animation: float 3s ease-in-out infinite;
    }
  `;
  if (!document.head.querySelector('style[data-shop-animations]')) {
    style.setAttribute('data-shop-animations', 'true');
    document.head.appendChild(style);
  }
} 